import * as React from "react";
import { ConversationMessage, MessageFile, SearchSource, AttachmentRef } from "@lupp/shared";
import { Markdown } from "./Markdown";
import { CopyIcon, EditIcon, TrashIcon } from "./icons";
import { dedupeSourcesForDisplay } from "../services/sources";
import { copyMarkdownRich } from "../services/clipboard";

export interface MessageBubbleProps {
  message: ConversationMessage;
  isLast?: boolean;
  onSend?: (text: string) => void;
  onSwitchBranch?: (messageId: string, dir: -1 | 1) => void;
  onEditSave?: (messageId: string, text: string) => void;
  onEditResend?: (messageId: string, text: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  /** Öffnet die Änderungsvorschläge-Sidebar für DIESE Nachricht (vom Changes-Bullet). */
  onOpenProposals?: (messageId: string) => void;
}

/* Parses basic markdown-ish content into React nodes:
 * - blank-line separated paragraphs
 * - "- item" lists
 * No external dep, keeps it simple for the MVP. */
/* ── Thinking / Reasoning block ─────────────────────────── */
interface ThinkingBlockProps {
  steps: string[];
  isStreaming: boolean;
  durationMs?: number;
}
interface ThinkingBlockState {
  open: boolean;
  elapsedSec: number;
  tooltip: { text: string; x: number; y: number } | null;
}

class ThinkingBlock extends React.Component<ThinkingBlockProps, ThinkingBlockState> {
  private timer: number | null = null;

  constructor(props) {
    super(props);
    this.state = { open: props.isStreaming, elapsedSec: 0, tooltip: null };
  }

  componentDidMount(): void {
    if (this.props.isStreaming) this.startTimer();
  }

  componentDidUpdate(prev: ThinkingBlockProps): void {
    if (!prev.isStreaming && this.props.isStreaming) this.startTimer();
    if (prev.isStreaming && !this.props.isStreaming) {
      this.stopTimer();
      this.setState({ open: false, tooltip: null });
    }
  }

  componentWillUnmount(): void {
    this.stopTimer();
  }

  startTimer(): void {
    if (this.timer) return;
    this.timer = window.setInterval(() => {
      this.setState((s) => ({ elapsedSec: s.elapsedSec + 1 }));
    }, 1000);
  }

  stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  showTooltip(step: string, e: React.MouseEvent): void {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.setState({ tooltip: { text: step, x: rect.left, y: rect.bottom + 6 } });
  }

  render(): JSX.Element {
    const { steps, isStreaming, durationMs } = this.props;
    const { open, elapsedSec, tooltip } = this.state;

    const sec = isStreaming ? elapsedSec : durationMs != null ? Math.round(durationMs / 1000) : null;

    const headerLabel = isStreaming
      ? `Nachdenken … ${sec}s`
      : sec != null
      ? `Nachgedacht für ${sec === 1 ? "1 Sekunde" : `${sec} Sekunden`}`
      : "Gedankengang";

    return (
      <div className="thinking-block">
        <button
          className="thinking-block__toggle"
          type="button"
          onClick={(): void => this.setState((s) => ({ open: !s.open, tooltip: s.open ? null : s.tooltip }))}
          aria-expanded={open}
        >
          {isStreaming && (
            <span className="thinking-block__spinner" aria-hidden="true">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            </span>
          )}
          <span className="thinking-block__label">{headerLabel}</span>
          <svg
            className={`thinking-block__chevron${open ? " thinking-block__chevron--open" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div className="thinking-block__content">
            {steps.length > 0 ? (
              steps.map((step, i) => {
                const isToolCall = /^(🔍|🌐|📄|✏️|🔧)/.test(step);
                const isToolResult = step.startsWith("↩");
                return (
                  <div
                    key={i}
                    className={`ts-item${
                      isToolCall ? " ts-item--tool" : isToolResult ? " ts-item--result" : " ts-item--reasoning"
                    }`}
                    onMouseEnter={(e): void => this.showTooltip(step, e)}
                    onMouseLeave={(): void => this.setState({ tooltip: null })}
                  >
                    <span className="ts-item__dot" aria-hidden="true" />
                    <span className="ts-item__text">{step}</span>
                  </div>
                );
              })
            ) : (
              <div className="ts-item ts-item--muted">
                <span className="ts-item__dot" aria-hidden="true" />
                <span className="ts-item__text">
                  {isStreaming ? "Reasoning läuft …" : "Keine Zusammenfassung verfügbar."}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Tooltip portal-like — positioned fixed. Only while open: collapsing unmounts
            the steps, so their onMouseLeave never fires and the tooltip would linger. */}
        {open && tooltip && (
          <div
            className="ts-tooltip"
            style={{ top: tooltip.y, left: Math.max(8, tooltip.x) }}
            onMouseLeave={(): void => this.setState({ tooltip: null })}
          >
            {tooltip.text}
          </div>
        )}
      </div>
    );
  }
}

/* ── File chip shown inside a user message ───────────────── */
function msgFileIcon(mime: string, name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const t = mime || "";
  if (t === "application/pdf" || ext === "pdf") return "pdf";
  if (t.includes("word") || ext === "doc" || ext === "docx") return "word";
  if (t.includes("excel") || ext === "xls" || ext === "xlsx") return "excel";
  if (t === "text/csv" || ext === "csv") return "csv";
  if (t.includes("powerpoint") || ext === "ppt" || ext === "pptx") return "ppt";
  if (t.startsWith("image/")) return "image";
  if (
    t.includes("json") ||
    t.includes("html") ||
    t.includes("xml") ||
    ext === "json" ||
    ext === "html" ||
    ext === "xml"
  )
    return "code";
  if (t === "text/plain" || t === "text/markdown" || ext === "txt" || ext === "md") return "text";
  if (t.includes("zip") || ext === "zip") return "zip";
  return "file";
}

function MsgFileChipIcon({ type }: { type: string }): JSX.Element {
  const s = {
    width: 13,
    height: 13,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (type) {
    case "pdf":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </svg>
      );
    case "word":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <polyline points="9 15 12 18 15 15" />
        </svg>
      );
    case "excel":
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="16" y2="17" />
        </svg>
      );
    case "image":
      return (
        <svg {...s}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case "code":
      return (
        <svg {...s}>
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      );
    default:
      return (
        <svg {...s}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
  }
}

function MsgFileChip({ f }: { f: MessageFile }): JSX.Element {
  const type = msgFileIcon(f.mime, f.name);
  const short = f.name.length > 14 ? f.name.slice(0, 12) + "…" : f.name;
  return (
    <div className="msg-file-chip" title={f.name}>
      <span className="msg-file-chip__icon">
        <MsgFileChipIcon type={type} />
      </span>
      <span className="msg-file-chip__name">{short}</span>
      <span className="msg-file-chip__size">{f.size}</span>
    </div>
  );
}

/* ── Web search + knowledge sources block ────────────────── */
function faviconUrl(url: string): string {
  return `https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(url)}`;
}
function isWebSource(s: SearchSource): boolean {
  return /^https?:/i.test(s.url);
}
/** (Text-)file icon for knowledge-file sources (matches the inline citation icon). */
function FileSourceIcon({ className }: { className?: string }): JSX.Element {
  return (
    <span className={className}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M16 13H8" />
        <path d="M16 17H8" />
        <path d="M10 9H8" />
      </svg>
    </span>
  );
}

interface SourcesBlockProps {
  sources: SearchSource[];
  /** Whether inline citation markers are currently hidden in the message. */
  citationsHidden: boolean;
  /** Toggle show/hide of the inline citations in the message. */
  onToggleCitations: () => void;
}
interface SourcesBlockState {
  open: boolean;
}
class SourcesBlock extends React.Component<SourcesBlockProps, SourcesBlockState> {
  constructor(props: SourcesBlockProps) {
    super(props);
    this.state = { open: false };
  }
  render(): JSX.Element {
    const { open } = this.state;
    const { citationsHidden, onToggleCitations } = this.props;
    // Display-only dedupe: same file/hash → one source (web results unaffected).
    const sources = dedupeSourcesForDisplay(this.props.sources);
    const faviconCount = Math.min(sources.length, 3);
    const extra = sources.length - faviconCount;
    return (
      <div
        className={`sources-block${citationsHidden ? " sources-block--citations-hidden" : ""}`}
        title={citationsHidden ? "Achtung! Quellenangaben sind aktuell ausgeblendet" : undefined}
      >
        <div className="sources-block__bar">
        <button
          className="sources-block__toggle"
          type="button"
          onClick={(): void => this.setState((s) => ({ open: !s.open }))}
          aria-expanded={open}
          aria-label={`${sources.length} Quellen umschalten`}
        >
          <span className="sources-block__favicons">
            {sources.slice(0, faviconCount).map((s, i) =>
              isWebSource(s) ? (
                <img
                  key={i}
                  className="sources-block__favicon"
                  src={faviconUrl(s.url)}
                  alt=""
                  width="16"
                  height="16"
                  onError={(e): void => {
                    (e.target as HTMLImageElement).style.visibility = "hidden";
                  }}
                />
              ) : (
                <FileSourceIcon key={i} className="sources-block__favicon sources-block__favicon--icon" />
              ),
            )}
            {extra > 0 && <span className="sources-block__more" aria-hidden="true">+{extra}</span>}
          </span>
          <span className="sources-block__count">
            {sources.length} Quelle{sources.length !== 1 ? "n" : ""}
            {citationsHidden && (
              <span className="sources-block__alert" title="Quellenangaben ausgeblendet" aria-label="Quellenangaben ausgeblendet">
                !
              </span>
            )}
          </span>
        </button>
        <button
          className="sources-block__visibility"
          type="button"
          onClick={onToggleCitations}
          title={citationsHidden ? "Quellenangaben anzeigen" : "Quellenangaben verstecken"}
          aria-label={citationsHidden ? "Quellenangaben anzeigen" : "Quellenangaben verstecken"}
        >
          <span className="sources-block__visibility-label">
            <bdi className="sources-block__visibility-text">
              {citationsHidden ? "Quellenangaben anzeigen" : "Quellenangaben verstecken"}
            </bdi>
          </span>
        </button>
        </div>
        {open && (
          <div className="sources-block__list">
            {sources.map((s, i) =>
              isWebSource(s) ? (
                <a
                  key={i}
                  className="sources-block__item"
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title || s.url}
                  aria-label={`Quelle anzeigen: ${s.url}`}
                >
                  <span className="sources-block__index">{i + 1}</span>
                  <img
                    className="sources-block__item-icon sources-block__item-favicon"
                    src={faviconUrl(s.url)}
                    alt=""
                    width="16"
                    height="16"
                    onError={(e): void => {
                      (e.target as HTMLImageElement).style.visibility = "hidden";
                    }}
                  />
                  <span className="sources-block__url">{s.url}</span>
                </a>
              ) : (
                <div key={i} className="sources-block__item sources-block__item--file" title={s.title}>
                  <span className="sources-block__index">{i + 1}</span>
                  <FileSourceIcon className="sources-block__item-icon" />
                  <span className="sources-block__url">{s.title}</span>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    );
  }
}

/* Änderungsvorschläge-Bullet unter der Assistant-Nachricht — wie eine Quellenangabe.
 * Klick öffnet die Vorschlags-Sidebar. Blinkt beim Auftauchen kurz (CSS-Animation beim Mount). */
function ChangesBullet({ count, resolved, onClick }: { count: number; resolved: number; onClick: () => void }): JSX.Element {
  const allDone = resolved >= count;
  // Blinken nur, solange noch NICHTS bearbeitet wurde (0 von N aufgelöst).
  const blink = resolved === 0;
  return (
    <div className="changes-bullet-row">
      <button className={`changes-bullet${blink ? " changes-bullet--blink" : ""}`} type="button" onClick={onClick} aria-label={`${count} Änderungsvorschläge anzeigen`}>
        <svg className="changes-bullet__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
        <span className="changes-bullet__count">
          {count} Änderung{count !== 1 ? "en" : ""}
        </span>
        {resolved > 0 && (
          <span className={`changes-bullet__badge${allDone ? " changes-bullet__badge--done" : ""}`}>
            {resolved}/{count}
          </span>
        )}
      </button>
    </div>
  );
}

/* Fork indicator (‹ 1/2 ›) under a message that has sibling branches. */
function BranchSwitcher({
  branch,
  onSwitch,
}: {
  branch: { index: number; count: number };
  onSwitch: (dir: -1 | 1) => void;
}): JSX.Element {
  return (
    <div className="branch-switcher" dir="ltr">
      <button
        className="branch-switcher__btn"
        type="button"
        aria-label="Vorherige Variante"
        disabled={branch.index <= 1}
        onClick={(): void => onSwitch(-1)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
      </button>
      <div className="branch-switcher__count">
        {branch.index}/{branch.count}
      </div>
      <button
        className="branch-switcher__btn"
        type="button"
        aria-label="Nächste Variante"
        disabled={branch.index >= branch.count}
        onClick={(): void => onSwitch(1)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </button>
    </div>
  );
}

/* Chip for a restored attachment (collection / note / chat / file / website). */
function AttachmentChip({ att }: { att: AttachmentRef }): JSX.Element {
  const isText = att.type === "text";
  const isCollection = att.type === "collection";
  const isFile = att.type === "file";
  const label = isText ? att.url : att.name ?? att.id;
  // Fixed-width chip → let CSS truncate the name; the full label lives in the tooltip.
  const short = label;
  const title = isText ? "Webseite" : isFile ? "Datei" : "Wissensquelle";
  const size = isFile ? att.size : undefined;
  return (
    <span className="file-chip file-chip--ready" title={label}>
      <span className="file-chip__icon" title={title}>
        {isText ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        ) : isCollection ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M5 12V18C5 18 5 21 12 21C19 21 19 18 19 18V12" />
            <path d="M5 6V12C5 12 5 15 12 15C19 15 19 12 19 12V6" />
            <path d="M12 3C19 3 19 6 19 6C19 6 19 9 12 9C5 9 5 6 5 6C5 6 5 3 12 3Z" />
          </svg>
        ) : isFile ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <path d="M10 9H8" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.76.76 1.23 1.52 1.41 2.5" />
          </svg>
        )}
      </span>
      <span className="file-chip__name">{short}</span>
      {size && <span className="file-chip__size">{size}</span>}
    </span>
  );
}

function ModelIcon({ image }: { image?: string }): JSX.Element {
  if (image) {
    return <img className="msg-avatar-img" src={image} alt="" width="26" height="26" />;
  }
  return <img src="assets/azure-ai.webp" alt="" width="26" height="26" style={{ objectFit: "contain" }} />;
}

/** The visible assistant text only: drop the followups section + inline citation markers. */
export function visibleAssistantText(content: string): string {
  return content
    .split(/\n\nFolgefragen\n/)[0]
    .replace(/[ \t]*\[(\d+)(?::[^\]]*)?\]/g, "") // remove [n] / [n:Zitat] citation markers
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/* Action icon buttons shown below each assistant response */
function ActionBar({ message }: { message: ConversationMessage }): JSX.Element {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = (): void => {
    // Copy only the visible answer (no thinking/sources/citation markers), as rich text.
    copyMarkdownRich(visibleAssistantText(message.content ?? ""))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard API not available */
      });
  };

  return (
    <div className="msg-actions">
      {/* 1 Bearbeiten */}
      <button className="msg-action-btn" type="button" aria-label="Bearbeiten" title="Bearbeiten">
        <EditIcon size={14} />
      </button>
      {/* 2 Kopieren */}
      <button
        className="msg-action-btn"
        type="button"
        aria-label={copied ? "Kopiert!" : "Kopieren"}
        title={copied ? "Kopiert!" : "Kopieren"}
        onClick={handleCopy}
        style={copied ? { color: "#4ade80" } : {}}
      >
        <CopyIcon />
      </button>
      {/* TODO(reactivate): Diese Aktionen sind noch ohne Funktion und vorübergehend
          ausgeblendet — wieder einblenden, sobald implementiert:
          (3) Vorlesen/TTS, (4) Gute Antwort, (5) Schlechte Antwort,
          (6) Antwort fortsetzen, (7) Neu generieren.
      {/* 3 Vorlesen *}
      <button className="msg-action-btn" type="button" aria-label="Vorlesen" title="Vorlesen">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
      {/* 4 Daumen hoch *}
      <button className="msg-action-btn" type="button" aria-label="Gute Antwort" title="Gute Antwort">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
        </svg>
      </button>
      {/* 5 Daumen runter *}
      <button className="msg-action-btn" type="button" aria-label="Schlechte Antwort" title="Schlechte Antwort">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
          <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
        </svg>
      </button>
      {/* 6 Antwort fortsetzen *}
      <button className="msg-action-btn" type="button" aria-label="Antwort fortsetzen" title="Antwort fortsetzen">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <polygon points="10 8 16 12 10 16 10 8" />
        </svg>
      </button>
      {/* 7 Neu generieren *}
      <button className="msg-action-btn" type="button" aria-label="Neu generieren" title="Neu generieren">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M23 4v6h-6" />
          <path d="M1 20v-6h6" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </button>
      */}
    </div>
  );
}

/* Re-export useState so ActionBar can use it as a plain function */

interface MessageBubbleState {
  editing: boolean;
  editText: string;
  copied: boolean;
  citationsHidden: boolean;
}

export default class MessageBubble extends React.Component<MessageBubbleProps, MessageBubbleState> {
  state: MessageBubbleState = { editing: false, editText: "", copied: false, citationsHidden: false };

  private copyUser = (): void => {
    copyMarkdownRich(this.props.message.content)
      .then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 1500);
      })
      .catch(() => {
        /* clipboard not available */
      });
  };

  private startEdit = (): void => this.setState({ editing: true, editText: this.props.message.content });
  private cancelEdit = (): void => this.setState({ editing: false });
  private saveEdit = (): void => {
    const text = this.state.editText.trim();
    this.setState({ editing: false });
    if (text && text !== this.props.message.content) this.props.onEditSave?.(this.props.message.id, text);
  };
  private resendEdit = (): void => {
    const text = this.state.editText.trim();
    this.setState({ editing: false });
    if (text) this.props.onEditResend?.(this.props.message.id, text);
  };

  render(): JSX.Element {
    const { message, isLast, onSend } = this.props;

    if (message.role === "user") {
      if (this.state.editing) {
        return (
          <div className="message--user" data-message-id={message.id}>
            <div className="edit-composer">
              <textarea
                className="edit-composer__textarea"
                value={this.state.editText}
                rows={Math.min(8, this.state.editText.split("\n").length + 1)}
                autoFocus
                onChange={(e): void => this.setState({ editText: e.target.value })}
              />
              <div className="edit-composer__actions">
                <button className="edit-composer__btn edit-composer__btn--ghost" type="button" onClick={this.saveEdit}>
                  Speichern
                </button>
                <div className="edit-composer__right">
                  <button className="edit-composer__btn edit-composer__btn--dark" type="button" onClick={this.cancelEdit}>
                    Abbrechen
                  </button>
                  <button className="edit-composer__btn edit-composer__btn--primary" type="button" onClick={this.resendEdit}>
                    Senden
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }
      const hasAttachmentsAbove =
        (message.files?.length ?? 0) > 0 || (message.attachments?.length ?? 0) > 0;
      return (
        <div className="message--user" data-message-id={message.id}>
          {message.timestampLabel && (
            <span className="msg-timestamp msg-timestamp--user">{message.timestampLabel}</span>
          )}
          {message.files && message.files.length > 0 && (
            <div className="msg-files">
              {message.files.map((f, i) => (
                <MsgFileChip key={i} f={f} />
              ))}
            </div>
          )}
          {message.attachments && message.attachments.length > 0 && (
            <div className="msg-files msg-files--attachments">
              {message.attachments.map((a, i) => (
                <AttachmentChip key={i} att={a} />
              ))}
            </div>
          )}
          <div className={`msg-bubble${hasAttachmentsAbove ? " msg-bubble--attached" : ""}`}>{message.content}</div>
          <div className="msg-user-footer">
            <div className="msg-user-actions">
              {this.props.onEditSave && (
                <button className="msg-action-btn" type="button" aria-label="Bearbeiten" title="Bearbeiten" onClick={this.startEdit}>
                  <EditIcon size={14} />
                </button>
              )}
              <button
                className="msg-action-btn"
                type="button"
                aria-label={this.state.copied ? "Kopiert!" : "Kopieren"}
                title={this.state.copied ? "Kopiert!" : "Kopieren"}
                onClick={this.copyUser}
                style={this.state.copied ? { color: "#4ade80" } : {}}
              >
                <CopyIcon size={14} />
              </button>
              {this.props.onDeleteMessage && (
                <button
                  className="msg-action-btn"
                  type="button"
                  aria-label="Löschen"
                  title="Löschen"
                  onClick={(): void => this.props.onDeleteMessage!(message.id)}
                >
                  <TrashIcon size={14} />
                </button>
              )}
            </div>
            {message.branch && this.props.onSwitchBranch && (
              <BranchSwitcher branch={message.branch} onSwitch={(dir): void => this.props.onSwitchBranch!(message.id, dir)} />
            )}
          </div>
        </div>
      );
    }

    const followupMatch = message.content.match(/^([\s\S]*?)\n\nFolgefragen\n([\s\S]+)$/);
    const bodyText = followupMatch ? followupMatch[1] : message.content;
    const followups: string[] = followupMatch ? followupMatch[2].split("\n").filter((l) => l.trim()) : [];

    const hasThinking = message.thinkingSteps && message.thinkingSteps.length > 0;
    const isStreaming = !!message.isStreaming;
    const isAborted = !!message.isAborted;

    return (
      <div className={`message--assistant${isLast ? "" : " message--assistant--hoverable"}`} data-message-id={message.id}>
        <div className="msg-header">
          <span className="msg-avatar">
            <ModelIcon image={message.modelImage} />
          </span>
          <span className="msg-name">{message.modelName ?? "Azure AI"}</span>
          {message.timestampLabel && !isStreaming && (
            <span className="msg-timestamp msg-timestamp--assistant">{message.timestampLabel}</span>
          )}
        </div>

        {/* Thinking / Reasoning block */}
        {(hasThinking || isStreaming) && (
          <ThinkingBlock
            steps={message.thinkingSteps ?? []}
            isStreaming={isStreaming}
            durationMs={message.thinkingDurationMs}
          />
        )}

        {/* Aborted badge */}
        {isAborted && (
          <div className="msg-aborted">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            Anfrage abgebrochen
          </div>
        )}

        {/* Main body — hidden while still streaming with no content yet */}
        {!isAborted && bodyText ? (
          <Markdown
            className={`msg-body${this.state.citationsHidden ? " msg-body--citations-hidden" : ""}`}
            content={bodyText}
            citations={message.sources}
            enhanceCode={!isStreaming}
          />
        ) : !isAborted && isStreaming ? null : null}

        {/* Web search + knowledge sources — shown live; sits above the message actions */}
        {message.sources && message.sources.length > 0 && (
          <SourcesBlock
            sources={message.sources}
            citationsHidden={this.state.citationsHidden}
            onToggleCitations={(): void => this.setState((s) => ({ citationsHidden: !s.citationsHidden }))}
          />
        )}

        {/* Änderungsvorschläge — Bullet wie eine Quellenangabe; Klick öffnet die Sidebar. */}
        {message.changeProposals && message.changeProposals.length > 0 && this.props.onOpenProposals && (
          <ChangesBullet
            count={message.changeProposals.length}
            resolved={Object.keys(message.changeStates ?? {}).length}
            onClick={(): void => this.props.onOpenProposals!(message.id)}
          />
        )}

        {!isStreaming && !isAborted && <ActionBar message={message} />}

        {message.branch && this.props.onSwitchBranch && (
          <BranchSwitcher branch={message.branch} onSwitch={(dir): void => this.props.onSwitchBranch!(message.id, dir)} />
        )}

        {isLast && !isStreaming && followups.length > 0 && (
          <div className="followups">
            <div className="followups__label">Folgefragen</div>
            {followups.map((q, i) => (
              <button
                key={i}
                className="followup-btn"
                type="button"
                onClick={(): void => {
                  onSend && onSend(q);
                }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
}
